import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import useApp from "../../../utils/useApp";
import { useLocation } from "react-router-dom";
import { currentUrl } from "../../../App";
import { fetchAssessmentTypes, fetchGoalTemplates, fetchNoteTypes, fetchPrograms } from "../../../services/httpClient";
import Programs from "./Programs";
import ProgramEditor from "./ProgramEditor";
import { CatalogOption } from "./ProgramLevelCard";

interface OrgProgramsState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgPrograms = ({ setOrgToSave, setShowToast, setToastText, setToastBody }: OrgProgramsState) => {
  const { auth, showMenu }: any = useApp();
  const location = useLocation();
  currentUrl.value = location.pathname;
  const [errMsg, setErrMsg] = useState("");
  const [programs, setPrograms] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<any>(null);
  const [goalTemplateCatalog, setGoalTemplateCatalog] = useState<CatalogOption[]>([]);
  const [assessmentTypeCatalog, setAssessmentTypeCatalog] = useState<CatalogOption[]>([]);
  const [noteTypeCatalog, setNoteTypeCatalog] = useState<CatalogOption[]>([]);

  useEffect(() => {
    getPrograms();
    getCatalogs();
  }, [auth?.profile]);

  // catalogs are fetched once per page load and shared with every level card
  const getCatalogs = () => {
    const toOptions = (res: any) => (res?.[0]?.data || []).map((t: any) => ({ ...t, key: t.id }));
    fetchGoalTemplates(auth?.profile?.userOrgID)
      .then((res: any) => setGoalTemplateCatalog(toOptions(res)))
      .catch((err: any) => console.log(err));
    fetchAssessmentTypes()
      .then((res: any) =>
        // only published, non-archived assessment types can be attached to a level
        setAssessmentTypeCatalog(toOptions(res).filter((t: any) => t.is_published === 1 && t.is_archived !== 1)),
      )
      .catch((err: any) => console.log(err));
    fetchNoteTypes()
      .then((res: any) =>
        // only published, non-archived note types; default types are limited to user note types
        setNoteTypeCatalog(
          toOptions(res).filter((t: any) => t.is_published === 1 && !t.is_archived && (t.organization_id !== null || t.type === "user")),
        ),
      )
      .catch((err: any) => console.log(err));
  };

  const getPrograms = () => {
    fetchPrograms("full")
      .then((res: any) => {
        const progs = res[0].data;
        setPrograms(progs);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const editingProgram = programs.find((p: any) => p.id === editingId);

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {editingProgram ? (
            <ProgramEditor
              program={editingProgram}
              getItems={getPrograms}
              onDone={() => setEditingId(null)}
              setOrgToSave={setOrgToSave}
              setErrMessage={setErrMsg}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              goalTemplateCatalog={goalTemplateCatalog}
              assessmentTypeCatalog={assessmentTypeCatalog}
              noteTypeCatalog={noteTypeCatalog}
            />
          ) : (
            <>
              <Row className="mb-4">
                <Col xs={9}>
                  <h4 className="app-header-text">Programs</h4>
                </Col>
                <Col xs={3} className="d-flex justify-content-end">
                  <Button className="app-primary-color button" onClick={() => setShowAdd(true)}>
                    Create Program
                  </Button>
                </Col>
              </Row>
              <Programs
                programs={programs}
                getPrograms={getPrograms}
                setOrgToSave={setOrgToSave}
                setErrMessage={setErrMsg}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
                showAddProgram={showAdd}
                setShowAddProgram={setShowAdd}
                onEdit={setEditingId}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(OrgPrograms);
